import { useCallback } from "react";
import {
  type FunnelEventInput,
  useTrackFunnelEventMutation,
} from "@appcaire/graphql";
import {
  createAnalyticsId,
  getProviderFunnelSessionId,
} from "@appcaire/shared/seo";
import { getEnvVar } from "./env";

export type StockholmConsumerEvent =
  | "journey_select"
  | "filter_apply"
  | "shortlist_add"
  | "shortlist_remove"
  | "compare_view"
  | "share_shortlist"
  | "print_shortlist"
  | "provider_impression"
  | "provider_select"
  | "profile_view"
  | "contact_form_start"
  | "contact_click"
  | "seo_funnel_click";

export type StockholmAnalyticsJourney =
  | "ansoka"
  | "valja"
  | "byta"
  | "narstaende";

export type StockholmFilterType =
  | "search"
  | "district"
  | "organizational_form"
  | "language"
  | "competence"
  | "subcontractor"
  | "sort";

export type StockholmEventChannel =
  | "phone"
  | "email"
  | "website"
  | "official"
  | "native_share"
  | "clipboard";

export type StockholmSourceSurface =
  | "homepage"
  | "finder"
  | "district"
  | "provider"
  | "compare"
  | "guide"
  | "header"
  | "footer"
  | "shortlist";

/**
 * Deliberately closed analytics payload. There is no property for a search
 * query, contact detail, message or other visitor-authored free text.
 */
export interface StockholmEventData {
  provider_id?: string;
  provider_count?: number;
  journey?: StockholmAnalyticsJourney;
  filter_type?: StockholmFilterType;
  sort_id?: "name" | "caireIndex" | "satisfaction" | "continuity" | "relevance";
  source_surface?: StockholmSourceSurface;
  target_path?: string;
  channel?: StockholmEventChannel;
  list_position?: number;
}

export type TrackStockholmEvent = (
  event: StockholmConsumerEvent,
  data?: StockholmEventData,
) => void;

type AnalyticsWindow = Window & {
  dataLayer?: unknown[];
  gaInitialized?: boolean;
  gtag?: (...args: unknown[]) => void;
};

const SOURCE_SITE = "hemtjanstistockholm";
const MUNICIPALITY = "stockholm";
const GA_SCRIPT_ID = "stockholm-ga4-script";
const GA_MEASUREMENT_ID = /^G-[A-Z0-9]+$/i;
const SAFE_TOKEN = /^[a-zA-Z0-9:_-]{1,128}$/;
const SAFE_PATH = /^\/[a-zA-Z0-9/_-]{0,199}$/;
const JOURNEYS = new Set<StockholmAnalyticsJourney>([
  "ansoka",
  "valja",
  "byta",
  "narstaende",
]);
const FILTER_TYPES = new Set<StockholmFilterType>([
  "search",
  "district",
  "organizational_form",
  "language",
  "competence",
  "subcontractor",
  "sort",
]);
const SORT_IDS = new Set<NonNullable<StockholmEventData["sort_id"]>>([
  "name",
  "caireIndex",
  "satisfaction",
  "continuity",
  "relevance",
]);
const SOURCE_SURFACES = new Set<StockholmSourceSurface>([
  "homepage",
  "finder",
  "district",
  "provider",
  "compare",
  "guide",
  "header",
  "footer",
  "shortlist",
]);
const CHANNELS = new Set<StockholmEventChannel>([
  "phone",
  "email",
  "website",
  "official",
  "native_share",
  "clipboard",
]);
const PROVIDER_REQUIRED_EVENTS = new Set<StockholmConsumerEvent>([
  "shortlist_add",
  "shortlist_remove",
  "provider_impression",
  "provider_select",
  "profile_view",
  "contact_form_start",
  "contact_click",
]);

/**
 * Install the GA4 tag for the plain Vite development path.
 *
 * Production SSR installs the same tag before hydration and marks it as
 * initialized. The guards below therefore keep hydration and React StrictMode
 * from adding a second script or resetting the shared data layer.
 */
export function initStockholmGa(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const measurementId =
    getEnvVar("VITE_GA_ID") || getEnvVar("VITE_GA_MEASUREMENT_ID");
  if (!GA_MEASUREMENT_ID.test(measurementId)) return;

  const analyticsWindow = window as AnalyticsWindow;
  const existingScript = document.getElementById(GA_SCRIPT_ID);
  if (
    analyticsWindow.gaInitialized ||
    (typeof analyticsWindow.gtag === "function" && existingScript)
  ) {
    analyticsWindow.gaInitialized = true;
    return;
  }

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.gtag = (...args: unknown[]) => {
    analyticsWindow.dataLayer?.push(args);
  };
  analyticsWindow.gaInitialized = true;

  analyticsWindow.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  analyticsWindow.gtag("js", new Date());
  analyticsWindow.gtag("config", measurementId, {
    anonymize_ip: true,
    cookie_flags: "SameSite=None;Secure",
    send_page_view: false,
  });

  if (!existingScript) {
    const script = document.createElement("script");
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onerror = () => {
      analyticsWindow.gaInitialized = false;
      script.remove();
    };
    document.head.appendChild(script);
  }
}

function safeToken(value: unknown): string | undefined {
  return typeof value === "string" && SAFE_TOKEN.test(value)
    ? value
    : undefined;
}

function safeInteger(value: unknown, maximum: number): number | undefined {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= maximum
    ? value
    : undefined;
}

function safeTargetPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    const path = new URL(value, "https://hemtjanstistockholm.se").pathname;
    return SAFE_PATH.test(path) ? path : undefined;
  } catch {
    return undefined;
  }
}

/** Runtime allowlist complements the closed TypeScript payload at JS edges. */
function sanitizeEventData(data: StockholmEventData): StockholmEventData {
  const providerId = safeToken(data.provider_id);
  const providerCount = safeInteger(data.provider_count, 3);
  const journey = JOURNEYS.has(data.journey as StockholmAnalyticsJourney)
    ? data.journey
    : undefined;
  const filterType = FILTER_TYPES.has(data.filter_type as StockholmFilterType)
    ? data.filter_type
    : undefined;
  const sortId = SORT_IDS.has(
    data.sort_id as NonNullable<StockholmEventData["sort_id"]>,
  )
    ? data.sort_id
    : undefined;
  const sourceSurface = SOURCE_SURFACES.has(
    data.source_surface as StockholmSourceSurface,
  )
    ? data.source_surface
    : undefined;
  const channel = CHANNELS.has(data.channel as StockholmEventChannel)
    ? data.channel
    : undefined;
  const targetPath = safeTargetPath(data.target_path);
  const listPosition = safeInteger(data.list_position, 10_000);

  return {
    ...(providerId ? { provider_id: providerId } : {}),
    ...(providerCount !== undefined ? { provider_count: providerCount } : {}),
    ...(journey ? { journey } : {}),
    ...(filterType ? { filter_type: filterType } : {}),
    ...(sortId ? { sort_id: sortId } : {}),
    ...(sourceSurface ? { source_surface: sourceSurface } : {}),
    ...(targetPath ? { target_path: targetPath } : {}),
    ...(channel ? { channel } : {}),
    ...(listPosition !== undefined ? { list_position: listPosition } : {}),
  };
}

function emitStockholmGaEvent(
  event: StockholmConsumerEvent,
  data: StockholmEventData,
  eventId?: string,
): void {
  if (typeof window === "undefined") return;
  const gtag = (window as AnalyticsWindow).gtag;
  gtag?.("event", event, {
    source_site: SOURCE_SITE,
    municipality: MUNICIPALITY,
    ...(eventId ? { event_id: eventId } : {}),
    ...data,
  });
}

/** Client-only GA helper retained for callers that do not need persistence. */
export function emitStockholmEvent(
  event: StockholmConsumerEvent,
  data: StockholmEventData = {},
): void {
  emitStockholmGaEvent(event, sanitizeEventData(data));
}

/** Emit an SPA page view without forwarding query strings or hashes to GA. */
export function emitStockholmPageView(pathname: string): void {
  if (typeof window === "undefined") return;
  const path = safeTargetPath(pathname);
  if (!path) return;
  const gtag = (window as AnalyticsWindow).gtag;
  gtag?.("event", "page_view", {
    source_site: SOURCE_SITE,
    municipality: MUNICIPALITY,
    page_path: path,
    page_location: `${window.location.origin}${path}`,
  });
}

function firstPartyInput(
  event: StockholmConsumerEvent,
  data: StockholmEventData,
  eventId: string,
): FunnelEventInput | null {
  if (typeof window === "undefined") return null;
  if (PROVIDER_REQUIRED_EVENTS.has(event) && !data.provider_id) return null;

  return {
    eventId,
    eventType: event,
    sourceSite: SOURCE_SITE,
    // Pathname only: query strings can contain visitor-authored search text.
    sourcePath: safeTargetPath(window.location.pathname) ?? null,
    sourceSurface: data.source_surface ?? null,
    targetPath: data.target_path ?? null,
    anchorCluster: data.journey ?? null,
    providerId: data.provider_id ?? null,
    municipality: MUNICIPALITY,
    channel: data.channel ?? data.filter_type ?? null,
    listId: data.sort_id ? `sort:${data.sort_id}` : null,
    listPosition: data.list_position ?? null,
    sessionId: getProviderFunnelSessionId(),
    // Referrer hosts can contain user-specific subdomains; omit them here.
    referrerHost: null,
    // Do not copy UTM values: they are arbitrary query-string text.
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
  };
}

/**
 * GA + anonymous first-party tracking for Stockholm journey actions.
 *
 * Wiring: call once in a component (`const track =
 * useStockholmEventTracking()`) and use `track(event, safePayload)` instead of
 * `emitStockholmEvent`. The mutation is fire-and-forget and never affects UI.
 */
export function useStockholmEventTracking(): TrackStockholmEvent {
  const [trackFirstParty] = useTrackFunnelEventMutation();

  return useCallback(
    (event: StockholmConsumerEvent, data: StockholmEventData = {}) => {
      const safeData = sanitizeEventData(data);
      const eventId = createAnalyticsId(event);
      emitStockholmGaEvent(event, safeData, eventId);
      const input = firstPartyInput(event, safeData, eventId);
      if (!input) return;
      void trackFirstParty({ variables: { input } }).catch(() => undefined);
    },
    [trackFirstParty],
  );
}
